/**
 * Annotation Hooks
 *
 * React Query hooks for managing annotation data fetching, generation,
 * and caching. Provides real-time annotation data for transcripts.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAnnotations,
  getAnnotationSummary,
  getAnnotationsBySegment,
  generateAnnotations,
} from "@/services/annotations";
import type {
  AnnotationListResponse,
  AnnotationSummaryResponse,
  AnnotationsBySegmentResponse,
  GenerateAnnotationsRequest,
} from "@/types/annotation";

/**
 * Hook to fetch all annotations for a transcript
 *
 * Annotations are generated as part of the processing pipeline,
 * so they should be available when the reflection is marked complete.
 */
export function useAnnotations(
  transcriptId: string | undefined,
  options?: {
    therapyStyleId?: string;
    annotationType?: string;
    enabled?: boolean;
  }
) {
  return useQuery<AnnotationListResponse>({
    queryKey: [
      "annotations",
      transcriptId,
      options?.therapyStyleId,
      options?.annotationType,
    ],
    queryFn: () =>
      getAnnotations(transcriptId!, {
        therapyStyleId: options?.therapyStyleId,
        annotationType: options?.annotationType,
      }),
    enabled: options?.enabled !== false && !!transcriptId,
  });
}

/**
 * Hook to fetch annotation summary for a transcript
 */
export function useAnnotationSummary(
  transcriptId: string | undefined,
  therapyStyleId?: string,
  enabled = true
) {
  return useQuery<AnnotationSummaryResponse | null>({
    queryKey: ["annotations", transcriptId, "summary", therapyStyleId],
    queryFn: () => getAnnotationSummary(transcriptId!, therapyStyleId),
    enabled: enabled && !!transcriptId,
  });
}

/**
 * Hook to fetch annotations grouped by segment
 */
export function useAnnotationsBySegment(
  transcriptId: string | undefined,
  therapyStyleId?: string,
  enabled = true
) {
  return useQuery<AnnotationsBySegmentResponse[]>({
    queryKey: ["annotations", transcriptId, "by-segment", therapyStyleId],
    queryFn: () => getAnnotationsBySegment(transcriptId!, therapyStyleId),
    enabled: enabled && !!transcriptId,
  });
}

/**
 * Hook to generate annotations for a transcript
 */
export function useGenerateAnnotations(transcriptId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request?: GenerateAnnotationsRequest) =>
      generateAnnotations(transcriptId, request),
    onSuccess: () => {
      // Invalidate all annotation queries for this transcript
      queryClient.invalidateQueries({
        queryKey: ["annotations", transcriptId],
      });
    },
  });
}
